import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHead, Card } from "@/components/hub/Shell";
import CodeRain from "@/components/marketing/CodeRain";
import { invalidateSiteEffects } from "@/lib/siteEffects";
import { Loader2, Sparkles, Check, Eye } from "lucide-react";
import { toast } from "sonner";

const LEVELS = [
  { key: "whisper",  label: "Whisper",  desc: "Only if you look for it." },
  { key: "balanced", label: "Balanced", desc: "Clearly there, never competing." },
  { key: "showcase", label: "Showcase", desc: "Proper server-room energy." },
];

const BANDS = [
  { key: "pale", label: "Pale bands", sub: "Home (top), About, Pricing, Enterprise, Why Connexis, Inside Connexis, Docs" },
  { key: "navy", label: "Navy bands", sub: "Home (metrics), Partners, Lending network, Launch partner, Services" },
];

/**
 * Website look — the one place the falling-code field is controlled.
 * Off here means off everywhere on the public site within twenty seconds, nothing else touched.
 */
export default function SiteEffects() {
  const [cfg, setCfg] = useState(null);
  const [labels, setLabels] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/site-effects");
      setCfg(data.code_rain);
      setLabels(data.page_labels || {});
    } catch { toast.error("Could not read the website settings"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    const prev = cfg;
    setCfg({ ...cfg, ...patch, pages: { ...cfg.pages, ...patch.pages } });
    try {
      const { data } = await api.put("/admin/settings/site-effects", patch);
      setCfg(data.code_rain);
      invalidateSiteEffects();
      toast.success("Saved — the public site picks it up within 20 seconds");
    } catch (e) {
      setCfg(prev);
      toast.error(e?.response?.data?.detail || "That didn't save");
    } finally { setBusy(false); }
  };

  if (!cfg) {
    return <div className="grid place-items-center py-28 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;
  }

  const live = cfg.enabled;

  return (
    <div className="space-y-6 pb-28" data-testid="site-effects">
      <PageHead
        eyebrow="Website look"
        title="Falling code"
        sub="A field of 0s, 1s and miniature Connexis marks drifting behind the calm bands of the public site, threaded through the house navy, the Connexis blue and the pale blue. It never runs on phones, never for visitors who've asked for reduced motion, and it stops dead when the band scrolls out of view. Switch it off here and it's gone from every page."
      />

      <Card label={live ? "Running" : "Switched off"} Icon={Sparkles} tone={live ? "act" : "plain"} testId="site-effects-master">
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-slate-900">The effect, everywhere</div>
            <div className="mt-0.5 text-[12px] text-slate-500">
              {live ? "Live on the bands ticked below." : "Nothing is rendering anywhere on the public site."}
            </div>
          </div>
          <button onClick={() => save({ enabled: !live })} disabled={busy} role="switch" aria-checked={live}
                  data-testid="site-effects-toggle"
                  className={`relative h-7 w-12 shrink-0 rounded-[10px] transition-colors ${live ? "bg-[#0055FF]" : "bg-slate-200"} disabled:opacity-50`}>
            <span className={`absolute top-1 size-5 rounded-full bg-white shadow transition-all ${live ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </Card>

      <div className={`grid gap-4 lg:grid-cols-[1.15fr_1fr] ${live ? "" : "pointer-events-none opacity-45"}`}>
        <div className="space-y-4">
          {BANDS.map((b) => (
            <Card key={b.key} label={`How loud · ${b.label}`} Icon={Sparkles} testId={`site-effects-band-${b.key}`}>
              <div className="mt-1 text-[11px] text-slate-500">{b.sub}</div>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                {LEVELS.map((l) => {
                  const on = cfg[b.key] === l.key;
                  return (
                    <button key={l.key} onClick={() => save({ [b.key]: l.key })} disabled={busy}
                            data-testid={`site-effects-${b.key}-${l.key}`}
                            className={`rounded-xl border p-3 text-left transition-colors ${
                              on ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[12.5px] font-semibold ${on ? "text-[#0055FF]" : "text-slate-900"}`}>{l.label}</span>
                        {on && <Check className="size-3.5 text-[#0055FF]" />}
                      </div>
                      <div className="mt-1 text-[11px] leading-relaxed text-slate-500">{l.desc}</div>
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}

          <Card label="Where it appears" Icon={Eye} testId="site-effects-pages">
            <div className="mt-3 divide-y divide-slate-100">
              {Object.keys(cfg.pages).map((p) => (
                <label key={p} className="flex cursor-pointer items-center gap-3 py-2.5"
                       data-testid={`site-effects-page-${p}`}>
                  <input type="checkbox" checked={cfg.pages[p] !== false} disabled={busy}
                         onChange={(e) => save({ pages: { [p]: e.target.checked } })}
                         className="size-4 accent-[#0055FF]" data-testid={`site-effects-page-check-${p}`} />
                  <span className="text-[12.5px] text-slate-700">{labels[p] || p}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="site-effects-preview-light">
            <div className="border-b border-slate-100 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Preview · pale bands · {cfg.pale}
            </div>
            <div className="relative h-56 bg-white">
              {live && <CodeRain variant="light" intensity={cfg.pale} />}
              <div className="relative grid h-full place-items-center">
                <div className="rounded-xl border border-slate-200 bg-white/70 px-5 py-3 text-[12.5px] font-semibold text-slate-900 ring-1 ring-white/50 backdrop-blur-[10px]">
                  Live routing topology
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="site-effects-preview-dark">
            <div className="border-b border-slate-100 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Preview · navy bands · {cfg.navy}
            </div>
            <div className="relative h-56 bg-[#0B1220]">
              {live && <CodeRain variant="dark" intensity={cfg.navy} />}
              <div className="relative grid h-full place-items-center">
                <div className="text-[12.5px] font-semibold text-white/90">Who we're looking for</div>
              </div>
            </div>
          </div>
          <p className="px-1 text-[11px] leading-relaxed text-slate-400">
            The previews run at the same settings as the live site. If the boxes look still, the
            effect is switched off — or your machine is set to reduced motion, in which case
            visitors with that setting see nothing either.
          </p>
        </div>
      </div>
    </div>
  );
}
